import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, HostListener, Input, ViewChild } from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';
import { filter } from 'rxjs';
import { SortAndLimitPipe } from 'src/core/pipes/sortandlimit.pipe';
import { AuthService } from 'src/core/services/auth.service';
import { BookmarkService } from 'src/core/services/bookmark.service';
import { ChatService } from 'src/core/services/chat.service';
import { DataService } from 'src/core/services/data.service';
import { DaterangeService } from 'src/core/services/daterange.service';
import { DeviceService } from 'src/core/services/device.service';
import { BusinessPartnerAccessService } from 'src/core/services/business-partner-access.service';
import { LoadingService } from 'src/core/services/loading.service';
import { ModalService } from 'src/core/services/modal.service';
import { SelectedContentService } from 'src/core/services/selectedcontent.service';
import { SelectedSiteService } from 'src/core/services/selectedsite.service';
import { SelectedsystemService } from 'src/core/services/selectedsystem.service';
import { WidgetService } from 'src/core/services/widget.service';
import { environment } from 'src/environments/environment';
import { FunctionalLocationOverview } from 'src/core/types/funcloc.types';
import { ApiResponse } from 'src/core/types/general.types';
import { SelectedSite } from 'src/core/types/selectedSite.types';
import { ExactSystem, SelectedSystem, SystemIBase } from 'src/core/types/selectedSystem.types';

@Component({
  selector: 'app-funcloc',
  imports: [CommonModule, SortAndLimitPipe, TranslocoModule],
  templateUrl: './funcloc.component.html',
  styleUrl: './funcloc.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class FunclocComponent {

  @Input() type: 'device' | 'site' = 'device';

  constructor(
    private loadingService: LoadingService,
    private selectedSystemService: SelectedsystemService,
    private chatService: ChatService,
    private widgetService: WidgetService,
    private daterangeService: DaterangeService,
    private deviceService: DeviceService,
    private modalService: ModalService,
    private selectedSiteService: SelectedSiteService,
    private selectedContentService: SelectedContentService,
    private bookmarkService: BookmarkService,
    private authService: AuthService,
    private bpAccessService: BusinessPartnerAccessService,
  ) { }

  isLoading: boolean;
  isError: boolean;
  isEmpty: boolean;
  site: FunctionalLocationOverview;
  selectedSystem: SelectedSystem;
  selectedSite: SelectedSite;
  limit: number | null = 5;
  selectedRange: number;
  screenInfo$ = this.deviceService.screenInfo$;
  getDeviceType = this.deviceService.getDeviceType;
  isBookmarked: boolean = false;
  updatingBookmark: boolean = true;
  errorBookmark: boolean = false;

  @ViewChild('siteicon') siteicon: ElementRef;
  @ViewChild('sitemenu') sitemenu: ElementRef;
  siteOpen: boolean = false;

  @HostListener('document:click', ['$event'])
  handleClickOutside(event: Event): void {
    this.handleSystemsClick(event);
  }

  private handleSystemsClick(event: Event) {
    if (!this.sitemenu || !this.siteicon) return;

    const clickedInside = this.sitemenu.nativeElement.contains(event.target);
    const clickedIcon = this.siteicon.nativeElement.contains(event.target);
    if (!clickedInside && !clickedIcon && this.siteOpen) {
      this.siteOpen = false;
      this.siteicon.nativeElement.active = false;
    }
  }


  ngOnInit(): void {

    //Init for Device
    if (this.type === 'device') {
      this.loadingService.$isSiteLoading.subscribe((isSiteLoading) => {
        this.isLoading = isSiteLoading;
      });
      this.loadingService.$isSiteError.subscribe((isSiteError) => {
        this.isError = isSiteError;
      });
      this.loadingService.$isSiteEmpty.subscribe((isEmpty) => {
        this.isEmpty = isEmpty;
      });
      this.daterangeService.$selectedRangeFunctionLocation.subscribe((selectedRange) => {
        this.selectedRange = selectedRange;
      });
      this.selectedSystemService.$siteoverview.subscribe((siteoverview) => {
        this.site = siteoverview;
      });
      this.selectedSystem = this.selectedContentService.currentSystem;
    }

    //Init for Site
    if (this.type === 'site') {
      this.loadingService.$isSiteLoadingSite.subscribe((isSiteLoading) => {
        this.isLoading = isSiteLoading;
      });
      this.loadingService.$isSiteErrorSite.subscribe((isSiteError) => {
        this.isError = isSiteError;
      });
      this.loadingService.$isSiteEmptySite.subscribe((isEmpty) => {
        this.isEmpty = isEmpty;
      });
      this.daterangeService.$selectedRangeFunctionLocationSite.subscribe((selectedRange) => {
        this.selectedRange = selectedRange;
      });
      this.selectedSiteService.$siteoverview.subscribe((siteoverview) => {
        this.site = siteoverview;
      });
      this.selectedContentService.selectedSite$
        .pipe(filter((s): s is SelectedSite => !!s))
        .subscribe((selectedSite) => {

          this.selectedSite = selectedSite;

          let body = {
            accountId: this.authService.userData.basic["id"],
            siteId: selectedSite["Id"]
          }
          if((environment.businessPartnerMode && (this.bpAccessService.access?.serviceData || this.bpAccessService.access?.machineData)) || !environment.businessPartnerMode) {
          this.bookmarkService.getBookmarkSite(body).subscribe({
            next: (data: any[]) => {
              if (data.length > 0) {
                this.isBookmarked = true;
              } else {
                this.isBookmarked = false;
              }
              this.updatingBookmark = false;
            },
            error: (err) => {
              console.error("Error fetching bookmarks:", err);
              this.updatingBookmark = false;
              this.errorBookmark = true;
            }
          });
        }
        });

      this.bookmarkService.$bookmarks.subscribe((bookmarks) => {
        this.updatingBookmark = true;

        if (bookmarks.filter(f => f.siteId === this.selectedSite?.Id).length > 0) {
          this.isBookmarked = true;
        } else {
          this.isBookmarked = false;
        }

        this.updatingBookmark = false;
      });
    }
  }

  updateBookmark(event: any) {

    event["target"].disabled = true;
    this.updatingBookmark = true;

    let body = {
      accountId: this.authService.userData.basic["id"],
      siteId: this.selectedSite.Id
    }

    this.bookmarkService.updateBookmarkSite(body).subscribe({
      next: (data: any[]) => {
        if (data.filter((item) => item.siteId === body.siteId).length > 0) {
          this.isBookmarked = true;
        } else {
          this.isBookmarked = false;
        }
        event["target"].disabled = false;
        this.bookmarkService.$refreshBookmarks.next();
        this.updatingBookmark = false;
      },
      error: (err) => {
        console.error("Error updating bookmark:", err);
      }
    });
  }

  get bpCanMachineData(): boolean {
    return this.bpAccessService.canViewMachineData;
  }

  get bpCanServiceData(): boolean {
    return this.bpAccessService.canViewServiceData;
  }

  get hideSiteInstrumentList(): boolean {
    return this.type === 'site' && !this.bpCanMachineData && !this.bpCanServiceData;
  }

  get showSiteDetailsButton(): boolean {
    return this.type !== 'site' || !this.hideSiteInstrumentList;
  }

  get showSiteRangeDropdown(): boolean {
    return this.type !== 'site' || this.bpCanMachineData || this.bpCanServiceData;
  }

  retrieveNumberSystems(type) {
    if (type === "with") {
      return "(" + this.site.device_data.filter((instrument) =>
        instrument['num_autochecks'] + instrument['num_maintenance'] + instrument['num_notifications'] !== 0
      ).length + ")";
    }
    if (type === "without") {
      return "(" + this.site.device_data.filter((instrument) =>
        instrument['num_autochecks'] + instrument['num_maintenance'] + instrument['num_notifications'] === 0
      ).length + ")";
    }
    return 0
  }

  onMaximize(filter?: string, abort?: boolean) {

    if (abort) return;

    if (this.widgetService.$workareaSize.value !== "small" && this.widgetService.$selectedWidget.value === "site" && !filter) {
      this.widgetService.closeWidget();
      return;
    }

    if (!filter) {
      this.widgetService.expandWidget("site", null);
      return;
    }

    this.widgetService.expandWidget("site", filter);
  }

  checkWidgetIcon() {
    if (this.widgetService.$workareaSize.value !== "small" && this.widgetService.$selectedWidget.value === "site") {
      return "minus";
    } else {
      return "plus";
    }
  }

  changeRange(range) {
    console.log("Steffen", this.type);
    if (this.type === 'device') {
      this.daterangeService.$selectedRangeFunctionLocation.next(range);
      return;
    }
    this.daterangeService.$selectedRangeFunctionLocationSite.next(range);
  }

  openHelp(section) {
    this.modalService.$helpModalChanged.next({ modal: section, open: true });
  }

  openWithParams(params: Record<string, string>) {
    // Create a URL object from the current location
    const url = new URL(window.location.href);

    // Remove all existing query params
    url.search = '';

    // Add or update params
    Object.entries(params).forEach(([key, value]) => {
      url.searchParams.set(key, value);
    });

    // Open the new URL in a new browser tab
    window.open(url.toString(), "_blank");
  }

  getSystemHealthIssues(instrument) {
    if (!this.bpCanMachineData) {
      return 0;
    }

    if (this.type === 'site') {
      return instrument.system_health[this.selectedRange + '']["autochecks"]
        + instrument.system_health[this.selectedRange + '']["maintenance"]
        + instrument.system_health[this.selectedRange + '']["error_events"]
        + instrument.system_health[this.selectedRange + '']["qc"];
    }

    const machineIssues = instrument.system_health[this.selectedRange + '']["autochecks"]
      + instrument.system_health[this.selectedRange + '']["maintenance"];
    const serviceAlarms = this.bpCanServiceData
      ? (instrument.service_history?.[this.selectedRange + '']?.["alarms"] ?? 0)
      : 0;

    return machineIssues + serviceAlarms;
  }

  getServiceHistoryIssues(servicehistory) {
    if (!this.bpCanServiceData) {
      return 0;
    }

    return servicehistory[this.selectedRange + '']["notifications_open"]
  }

  getInstrumentStatus(instrument): 'success' | 'warning' | 'error' {
    const totalIssues = this.getSystemHealthIssues(instrument)
      + this.getServiceHistoryIssues(instrument.service_history);

    if (this.bpCanServiceData && instrument.totally_down) {
      return 'error';
    }

    if (totalIssues !== 0) {
      return 'warning';
    }

    return 'success';
  }


  toggleLimit() {
    // Toggle between 5 and null (null shows all)
    this.limit = this.limit === null ? 5 : null;

  }

  dynamicToggleLabel() {
    if (this.limit === 5) {
      return 'Show +' + (this.site.device_data?.length - 5)
    }
    return "Reduce to 5"
  }

  refresh() {
    const selection = this.selectedContentService.selection;
    if (selection.kind === 'site') {
      this.selectedSiteService.$reloadSelectedSite.next();
      return;
    }

    if (selection.kind === 'device') {
      this.selectedSystemService.$reloadSelectedSystem.next();
    }
  }

  openSite() {
    this.siteOpen = true;
  }

  siteClosed() {
    this.siteOpen = false;
  }
}